import {
  ErrorCode,
  RETRY_AFTER_REFRESH_CODES,
  SESSION_TERMINATING_CODES,
} from '@sunucupark/shared/lite';
import type { ApiError as ApiErrorBody } from '@sunucupark/shared';

/** Failures that never reach the wire: they happen in the client itself. */
export const ClientErrorCode = {
  /** The request could not leave the device or the answer never arrived. */
  NETWORK: 'NETWORK',
  /** Our own timeout fired. */
  TIMEOUT: 'TIMEOUT',
  /** The client knows it is offline and did not even try. */
  OFFLINE: 'OFFLINE',
  /** The caller cancelled the request (navigation, a newer query). */
  ABORTED: 'ABORTED',
  /** The answer did not match the contract in `@sunucupark/shared`. */
  PARSE: 'PARSE',
} as const;

export type ClientErrorCode = (typeof ClientErrorCode)[keyof typeof ClientErrorCode];

/** Every failure surfaced to the UI is an AppError whose code maps to an i18n key. */
export class AppError extends Error {
  readonly code: ErrorCode | ClientErrorCode;
  readonly status?: number;
  readonly requestId?: string;
  /** Seconds the server asked us to wait (`RATE_LIMITED`, `MAIL_SERVER_UNAVAILABLE`). */
  readonly retryAfter?: number;
  readonly details?: unknown;

  constructor(
    code: AppError['code'],
    message?: string,
    options: {
      status?: number;
      requestId?: string;
      retryAfter?: number;
      details?: unknown;
      cause?: unknown;
    } = {},
  ) {
    super(message ?? code, { cause: options.cause });
    this.name = 'AppError';
    this.code = code;
    this.status = options.status;
    this.requestId = options.requestId;
    this.retryAfter = options.retryAfter;
    this.details = options.details;
  }

  /** Builds the error from the gateway's error envelope. */
  static fromBody(body: ApiErrorBody, status: number): AppError {
    return new AppError(body.error.code, body.error.message, {
      status,
      requestId: body.error.requestId,
      retryAfter: body.error.retryAfter,
      details: body.error.details,
    });
  }

  /** Wraps anything thrown (fetch rejections, aborts, programming errors). */
  static from(error: unknown): AppError {
    if (error instanceof AppError) return error;
    if (error instanceof Error) {
      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        return new AppError(ClientErrorCode.ABORTED, error.message, { cause: error });
      }
      if (/network request failed|failed to fetch|load failed|networkerror/i.test(error.message)) {
        return new AppError(ClientErrorCode.NETWORK, error.message, { cause: error });
      }
      return new AppError(ErrorCode.INTERNAL_ERROR, error.message, { cause: error });
    }
    return new AppError(ErrorCode.INTERNAL_ERROR, String(error));
  }

  /** The access token can be refreshed once and the request repeated. */
  get shouldRefreshAndRetry(): boolean {
    return (RETRY_AFTER_REFRESH_CODES as readonly string[]).includes(this.code);
  }

  /** The session is over; the user has to sign in again. */
  get terminatesSession(): boolean {
    return (SESSION_TERMINATING_CODES as readonly string[]).includes(this.code);
  }

  /** Connectivity, not a server answer: the UI shows the offline state instead of an error. */
  get isOffline(): boolean {
    return (
      this.code === ClientErrorCode.NETWORK ||
      this.code === ClientErrorCode.OFFLINE ||
      this.code === ClientErrorCode.TIMEOUT
    );
  }

  /** The caller cancelled it, so nothing should be shown. */
  get isCancelled(): boolean {
    return this.code === ClientErrorCode.ABORTED;
  }

  /** i18n key of the user-facing message; see {@link errorResources}. */
  get messageKey(): string {
    switch (this.code) {
      case ClientErrorCode.NETWORK:
      case ClientErrorCode.OFFLINE:
        return 'errors.network';
      case ClientErrorCode.TIMEOUT:
        return 'errors.timeout';
      case ClientErrorCode.ABORTED:
      case ClientErrorCode.PARSE:
        return 'errors.generic';
      default:
        return `errors.${this.code}`;
    }
  }
}

export const isAppError = (error: unknown): error is AppError => error instanceof AppError;
export { ErrorCode };
